/**
 * Destination-based Washington sales tax.
 *
 * Washington is a DESTINATION-based state: retail sales tax is owed at the
 * combined state + local rate for the place where the buyer receives the
 * goods. For bulk material delivered by truck, that is the DELIVERY ADDRESS —
 * not our yard in Bonney Lake. Charging one flat rate off our own location
 * over- or under-collects on nearly every order, and the difference is real:
 * the spread across our service area is well over a full percentage point.
 *
 * Scope and caveats:
 *
 * 1. This module resolves the RATE only. The taxable BASE is assembled at
 *    checkout, and per DOR it includes delivery charges on a retail sale even
 *    when they are separately stated — so materials + delivery + the weekend
 *    surcharge are all taxed at the destination rate.
 *
 * 2. It is not the authority on the rate itself. The Department of Revenue
 *    publishes rates quarterly and they move. The table below is a snapshot
 *    for the cities we actually deliver to, and every figure must be verified
 *    against the DOR before it is used to collect money:
 *
 *      https://dor.wa.gov/taxes-rates/sales-and-use-tax-rates
 *
 *    The correct long-term fix is the DOR Tax Rate Lookup API, which returns
 *    the exact combined rate and location code for a street address. That call
 *    must happen server-side (it needs a key and cannot be exposed in the
 *    browser), so it is a hosting-side change. `resolveSalesTax` is shaped to
 *    be a drop-in for that call when it exists: same inputs, same output.
 */

/** Fallback when we cannot resolve the destination — the King/Pierce midpoint. */
export const DEFAULT_TAX_RATE = 0.101;

/**
 * Combined state + local rate by ZIP for the ZIPs in our service area.
 *
 * Keyed by ZIP because that is what a geocoder reliably returns, and because
 * within our footprint ZIP maps cleanly enough to a taxing jurisdiction. Where
 * a ZIP straddles a city line the DOR address lookup is authoritative; these
 * are estimates presented to the customer as such.
 *
 * VERIFY EVERY RATE AGAINST DOR BEFORE COLLECTING TAX.
 */
const ZIP_TAX_RATES: Record<string, number> = {
  // ---- Seattle ----
  '98101': 0.1035, '98102': 0.1035, '98103': 0.1035, '98104': 0.1035,
  '98105': 0.1035, '98106': 0.1035, '98107': 0.1035, '98108': 0.1035,
  '98109': 0.1035, '98112': 0.1035, '98115': 0.1035, '98116': 0.1035,
  '98117': 0.1035, '98118': 0.1035, '98119': 0.1035, '98122': 0.1035,
  '98125': 0.1035, '98126': 0.1035, '98133': 0.1035, '98134': 0.1035,
  '98136': 0.1035, '98144': 0.1035, '98146': 0.1025, '98148': 0.1025,
  '98155': 0.1035, '98164': 0.1035, '98177': 0.1035, '98178': 0.1025,
  '98199': 0.1035,
  // ---- Bellevue / Eastside ----
  '98004': 0.103, '98005': 0.103, '98006': 0.103, '98007': 0.103,
  '98008': 0.103, '98033': 0.103, '98034': 0.103, '98039': 0.103,
  '98040': 0.103, '98052': 0.103, '98053': 0.103, '98074': 0.103,
  '98075': 0.103, '98027': 0.103, '98029': 0.103, '98059': 0.103,
  // ---- South King ----
  '98030': 0.102, '98031': 0.102, '98032': 0.102, '98042': 0.102,
  '98055': 0.103, '98056': 0.103, '98057': 0.103, '98058': 0.103,
  '98001': 0.103, '98002': 0.103, '98003': 0.103, '98023': 0.103,
  '98092': 0.103, '98166': 0.102, '98168': 0.102, '98188': 0.102,
  '98198': 0.102, '98038': 0.102, '98010': 0.102, '98022': 0.093,
  '98065': 0.102, '98024': 0.102, '98045': 0.102, '98051': 0.093,
  // ---- Pierce ----
  '98402': 0.103, '98403': 0.103, '98404': 0.103, '98405': 0.103,
  '98406': 0.103, '98407': 0.103, '98408': 0.103, '98409': 0.103,
  '98418': 0.103, '98421': 0.103, '98422': 0.103, '98443': 0.103,
  '98444': 0.102, '98445': 0.102, '98446': 0.102, '98465': 0.103,
  '98466': 0.103, '98467': 0.103, '98371': 0.099, '98372': 0.099,
  '98373': 0.099, '98374': 0.099, '98375': 0.099, '98387': 0.099,
  '98390': 0.099, '98391': 0.089, '98332': 0.089, '98335': 0.089,
  '98329': 0.089, '98338': 0.099, '98424': 0.102, '98430': 0.099,
  '98433': 0.099, '98439': 0.099, '98498': 0.1, '98499': 0.1,
  '98439-lakewood': 0.1, '98388': 0.099, '98354': 0.099, '98351': 0.099,
}

/**
 * City-name fallback, used when a geocode gives us a locality but no usable
 * postcode. Coarser than the ZIP table — a city can contain more than one
 * taxing district — so it exists only to keep the estimate close.
 */
const CITY_TAX_RATES: Record<string, number> = {
  seattle: 0.1035,
  bellevue: 0.103,
  kirkland: 0.103,
  redmond: 0.103,
  sammamish: 0.103,
  issaquah: 0.103,
  'mercer island': 0.103,
  medina: 0.103,
  newcastle: 0.103,
  renton: 0.103,
  kent: 0.102,
  auburn: 0.103,
  'federal way': 0.103,
  burien: 0.102,
  tukwila: 0.102,
  seatac: 0.102,
  'des moines': 0.102,
  'maple valley': 0.102,
  covington: 0.102,
  'black diamond': 0.102,
  snoqualmie: 0.102,
  enumclaw: 0.093,
  tacoma: 0.103,
  puyallup: 0.099,
  sumner: 0.099,
  'bonney lake': 0.089,
  'gig harbor': 0.089,
  lakewood: 0.1,
}

export interface SalesTaxResult {
  /** Combined state + local rate as a decimal, e.g. 0.1035. */
  rate: number
  /** Human label for the jurisdiction the rate came from. */
  jurisdiction: string
  /** False when we fell back to the service-area default. */
  resolved: boolean
}

/** Pull a 5-digit WA ZIP out of a free-text address, if one is present. */
export function extractZip(address: string): string | null {
  const match = address.match(/\b(98\d{3})\b/)
  return match ? match[1] : null
}

/**
 * Resolve the sales tax rate for a delivery destination.
 *
 * Preference order: explicit postcode from the geocoder, then a ZIP typed into
 * the address, then the geocoded city name, then the service-area default.
 */
export function resolveSalesTax(
  address: string,
  geo?: { postcode?: string; city?: string },
): SalesTaxResult {
  const postcode = geo?.postcode?.match(/\b(98\d{3})\b/)?.[1] ?? extractZip(address || '')
  if (postcode && ZIP_TAX_RATES[postcode] !== undefined) {
    return {
      rate: ZIP_TAX_RATES[postcode],
      jurisdiction: postcode,
      resolved: true,
    }
  }
  const city = (geo?.city || '').trim().toLowerCase()
  if (city && CITY_TAX_RATES[city] !== undefined) {
    return {
      rate: CITY_TAX_RATES[city],
      jurisdiction: geo?.city as string,
      resolved: true,
    }
  }
  // Last resort: scan the typed address for a known city name. Customers often
  // type "1234 Main St, Kent" with no ZIP at all.
  const haystack = (address || '').toLowerCase()
  const hit = Object.keys(CITY_TAX_RATES)
    // Longest first so "des moines" wins over a stray "moines".
    .sort((a, b) => b.length - a.length)
    .find((name) => haystack.includes(name))
  if (hit) {
    return {
      rate: CITY_TAX_RATES[hit],
      jurisdiction: hit.replace(/\b\w/g, (c) => c.toUpperCase()),
      resolved: true,
    }
  }
  return {
    rate: DEFAULT_TAX_RATE,
    jurisdiction: 'King / Pierce County',
    resolved: false,
  }
}
